import { create } from 'zustand';
import apiClient from '../service/apiClient';
import {CssTemplateResponse, CssFont, CssImageRequest, BasicCreate, CssImageCreateRequest, CssImageTheme} from '../type/cssTemplate';

/**
 * @author pjh
 * @date 2025.08.19
 * @description CSS 템플릿 관리 스토어
 */

interface CssTemplateStoreData {
    cssTemplate: CssTemplateResponse | null;
    loading: boolean;
    selectedImages: Map<string, CssFont>; // cssName을 key로 선택된 이미지 관리
}

interface CssTemplateStoreAction {
    // 기본 템플릿 또는 프랜차이즈 전용 템플릿 조회
    getCssTemplate: (productId: string) => Promise<CssTemplateResponse>;

    // 템플릿 저장 (프랜차이즈 전용 템플릿 생성/수정 및 신규 이미지 등록)
    saveCssTemplate: (request: CssImageRequest[], productId: string, deleteCssNames: string[], mainCategoryName?: string | null) => Promise<void>;

    saveBasicCssTemplate: (create: CssImageCreateRequest[], productId: string, mainCategoryName?: string | null, existingThemes?: CssImageTheme[]) => Promise<void>;
    
    // 새로운 테마 저장 메서드
    saveNewThemes: (basicCreate: BasicCreate, productId: string) => Promise<void>;
    
    // 일반 유저용 다중 테마 업데이트 메서드
    updateThemesForUser: (
        updates: Array<{ themeId: string; isMain: boolean; images: Array<{ imageId?: string; fileId?: string }> }>,
        productId: string
    ) => Promise<void>;
}

export const useCssTemplateStore = create<CssTemplateStoreData & CssTemplateStoreAction>((set, get) => ({
    // 초기 상태
    cssTemplate: null,
    loading: false,
    selectedImages: new Map(),
    
    // 기본 템플릿 또는 프랜차이즈 전용 템플릿 조회
    getCssTemplate: async (productId?: string) => {
        set({ loading: true });
        try {
            const response = await apiClient.get(`/css/img/${productId}`);
            const template = response.data;
            
            set({
                cssTemplate: template,
                loading: false 
            });
            
            // 기존 이미지들을 selectedImages에 설정
            const selectedImages = new Map<string, CssFont>();
            if (template.categories) {
                template.categories.forEach((category: any) => {
                    category.images.forEach((image: CssFont) => {
                        selectedImages.set(image.cssName, image);
                    });
                });
            }
            set({ selectedImages });
            
            return template;
        } catch (error) {
            console.error('CSS 템플릿 조회 실패:', error);
            set({ loading: false });
            throw error;
        }
    },

    // 템플릿 저장 (프랜차이즈 전용 템플릿 생성/수정 및 신규 이미지 등록)
    saveCssTemplate: async (request: CssImageRequest[], productId: string, deleteCssNames: string[], mainCategoryName?: string | null) => {
        set({ loading: true });
        try {
            const requestBody = {
                update: request,
                deleteCssName: deleteCssNames,
                mainCategoryName: mainCategoryName
            };
            
            await apiClient.post(`/css/img/${productId}`, requestBody);

            set({ loading: false });
        } catch (error) {
            console.error('CSS 템플릿 저장 실패:', error);
            set({ loading: false });
            throw error;
        }
    },

    // 템플릿 저장 (관리자 전용 CSS 신규 이미지 등록)
    saveBasicCssTemplate: async (create: CssImageCreateRequest[], productId: string, mainCategoryName?: string | null, existingThemes?: CssImageTheme[]) => {
        set({ loading: true });
        try {
            // 테마별로 그룹화
            const themeMap = new Map<string, {themeId?: string, images: any[]}>();
            create.forEach(item => {
                const themeName = item.themeName;
                if (themeName && !themeMap.has(themeName)) {
                    // 기존 테마에서 themeId 찾기
                    const existingTheme = existingThemes?.find(t => t.themeName === themeName);
                    console.log(`테마 "${themeName}"에 대한 기존 테마 찾기:`, existingTheme);
                    themeMap.set(themeName, {
                        themeId: existingTheme?.themeId || undefined,
                        images: []
                    });
                }
                themeMap.get(themeName)?.images.push({
                    imageId: item.imageId || null, // imageId가 있으면 업데이트, 없으면 신규
                    cssName: item.cssName,
                    categoryName: item.categoryName || '',
                    fileId: item.fileId,
                    width: item.width,
                    height: item.height
                });
            });

            // BasicCreate 구조로 변환
            let basicCreate: BasicCreate = {
                themes: Array.from(themeMap.entries()).map(([themeName, data]) => ({
                    themeId: data.themeId, // 기존 테마의 경우 ID 포함
                    themeName,
                    isMain: themeName === (mainCategoryName || undefined),
                    images: data.images
                }))
            } as any;

            // 이미지 변경(create)이 없고 메인만 변경하려는 경우: existingThemes를 이용해 전체 테마 목록으로 isMain만 반영
            if ((create.length === 0 || themeMap.size === 0) && existingThemes && (mainCategoryName ?? null) !== null) {
                basicCreate = {
                    themes: existingThemes.map(t => ({
                        themeId: t.themeId,
                        themeName: t.themeName,
                        isMain: t.themeName === mainCategoryName,
                        images: []
                    }))
                } as any;
            }

            // BasicCreate를 직접 전송
            await apiClient.post(`/css/img/basic/${productId}`, basicCreate);

            set({ loading: false });
        } catch (error) {
            console.error('CSS 템플릿 저장 실패:', error);
            set({ loading: false });
            throw error;
        }
    },
    
    // 새로운 테마 저장 메서드
    saveNewThemes: async (basicCreate: BasicCreate, productId: string) => {
        set({ loading: true });
        try {
            // BasicCreate를 직접 전송
            await apiClient.post(`/css/img/basic/${productId}`, basicCreate);

            set({ loading: false });
        } catch (error) {
            console.error('테마 저장 실패:', error);
            set({ loading: false });
            throw error;
        }
    },

    // 일반 유저용 다중 테마 업데이트 메서드
    updateThemesForUser: async (
        updates: Array<{ themeId: string; isMain: boolean; images: Array<{ imageId?: string; fileId?: string }> }>,
        productId: string
    ) => {
        set({ loading: true });
        try {
            console.log('updateThemesForUser - 요청 데이터:', updates);
            await apiClient.post(`/css/img/${productId}`, updates);
            set({ loading: false });
        } catch (error) {
            console.error('다중 테마 업데이트 실패:', error);
            set({ loading: false });
            throw error;
        }
    }
}));
